import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../../../context/AppContext';
import useAppSettings from '../../../hooks/useAppSettings';
import LoadingScreen from '../../common/LoadingScreen/LoadingScreen';
import Slide1 from '../../slides/Slide1/Slide1';
import Slide3 from '../../slides/Slide3/Slide3';
import Slide4 from '../../slides/Slide4/Slide4';
import BackgroundSelect from '../../slides/BackgroundSelect/BackgroundSelect';
import ProcessingSlide from '../../slides/ProcessingSlide/ProcessingSlide';
import BoardingPass from '../../slides/BoardingPass/BoardingPass';
import Logo from '../../common/Logo/Logo';
import useAnalytics from '../../../hooks/useAnalytics';
import './BoardingPassFlow.scss';

const BoardingPassFlow = () => {
  const { state, actions } = useApp();
  const { homepageData, loading } = useAppSettings();
  const { currentSlide, userInfo, questionAnswers } = state;
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [slideDirection, setSlideDirection] = useState('next');
  const [animationPhase, setAnimationPhase] = useState('idle');
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [isPopupClosing, setIsPopupClosing] = useState(false);
  const { trackCoreValueSelected, trackSpecialtySelected, trackBackgroundSelected, trackBoardingPassMainMenu } = useAnalytics();

  const closeTimeoutRef = useRef(null);
  const scrollPositionRef = useRef(0);

  useEffect(() => {
    if (showExitPopup) {
      scrollPositionRef.current = window.scrollY;

      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPositionRef.current}px`;
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollPositionRef.current);
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [showExitPopup]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showExitPopup && !isPopupClosing) {
        handleClosePopup();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showExitPopup, isPopupClosing]);

  const handleClosePopup = () => {
    if (isPopupClosing) return;
    setIsPopupClosing(true);

    closeTimeoutRef.current = setTimeout(() => {
      setShowExitPopup(false);
      setIsPopupClosing(false);
    }, 350);
  };

  const handleBackToHome = () => {
    if (isPopupClosing) return;

    setShowExitPopup(false);
    setIsPopupClosing(false);

    if (currentSlide === 6) {
      trackBoardingPassMainMenu();
    }

    actions.resetExperience();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isPopupClosing) {
      handleClosePopup();
    }
  };

  const handlePopupCloseClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleClosePopup();
  };

  const handlePopupCloseTouch = (e) => {
    e.preventDefault(); 
    e.stopPropagation();
    handleClosePopup();
  };

  const handleConfirmClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleBackToHome();
  };

  const handleConfirmTouch = (e) => {
    e.preventDefault(); 
    e.stopPropagation();
    handleBackToHome();
  };

  if (loading) {
    return <LoadingScreen message="Loading experience..." />;
  }

  const slides = [
    {
      id: 1,
      component: Slide1,
      title: 'Welcome',
      canProceed: () => true,
      showInDots: false
    },
    {
      id: 2,
      component: Slide3,
      title: 'Core Value',
      canProceed: () => questionAnswers.q1 !== null,
      showInDots: true
    },
    {
      id: 3,
      component: Slide4,
      title: 'Specialty',
      canProceed: () => questionAnswers.q2 !== null,
      showInDots: true
    },
    {
      id: 4,
      component: BackgroundSelect,
      title: 'Background Select',
      canProceed: () => !!userInfo.selectedBackground,
      showInDots: true
    },
    {
      id: 5,
      component: ProcessingSlide,
      title: 'Processing',
      canProceed: () => true,
      showInDots: false
    },
    {
      id: 6,
      component: BoardingPass,
      title: 'Your Boarding Pass',
      canProceed: () => true,
      showInDots: false
    }
  ];

  const dotSlides = slides.filter((slide) => slide.showInDots);
  const currentSlideData = slides.find((slide) => slide.id === currentSlide);
  const CurrentSlideComponent = currentSlideData?.component;

  const handleNext = async () => {
    if (isTransitioning) return;

    const current = slides.find((s) => s.id === currentSlide);
    if (current && !current.canProceed()) return;

    const getCurrentQuestionValue = (questionAnswers) => {
      if (questionAnswers.q1 && questionAnswers.q2) {
        return { question: 'q2', value: questionAnswers.q2 };
      }

      if (questionAnswers.q1) {
        return { question: 'q1', value: questionAnswers.q1 };
      }

      return null;
    };

    const currentQ = getCurrentQuestionValue(questionAnswers);

    if (currentQ) {
      if (currentQ.question === 'q1' && currentSlide < '4') {
        trackCoreValueSelected(currentQ.value);
      } else if (currentQ.question === 'q2' && currentSlide < '4') {
        trackSpecialtySelected(currentQ.value);
      }
    }

    if (currentSlide) {
      if (currentSlide === 4) {
        trackBackgroundSelected(userInfo.selectedBackground.name);
      }
    }

    setIsTransitioning(true);

    if (currentSlide === 1) {
      setAnimationPhase('slide1-exit');

      setTimeout(() => {
        actions.setSlide(2);
        setAnimationPhase('slide2-enter');
      }, 500);

      setTimeout(() => {
        setAnimationPhase('idle');
        setIsTransitioning(false);
      }, 2500);
    } else {
      setSlideDirection('next');
      setAnimationPhase('sliding');

      setTimeout(() => {
        actions.setSlide(currentSlide + 1);
      }, 300);

      setTimeout(() => {
        setAnimationPhase('idle');
        setIsTransitioning(false);
      }, 600);
    }
  };

  const handlePrevious = async () => {
    if (isTransitioning || currentSlide === 1) return;

    setIsTransitioning(true);

    if (currentSlide === 2) {
      setAnimationPhase('slide2-exit');

      setTimeout(() => {
        actions.setSlide(1);
        setAnimationPhase('slide1-enter');
      }, 500);

      setTimeout(() => {
        setAnimationPhase('idle');
        setIsTransitioning(false);
      }, 1000);
    } else {
      setSlideDirection('prev');
      setAnimationPhase('sliding');

      setTimeout(() => {
        actions.setSlide(currentSlide - 1);
      }, 300);

      setTimeout(() => {
        setAnimationPhase('idle');
        setIsTransitioning(false);
      }, 600);
    }
  };

  const canProceedFromCurrent = currentSlideData?.canProceed() || false;

  const getCurrentDotIndex = () => {
    const currentSlideInDots = dotSlides.find(
      (slide) => slide.id === currentSlide
    );
    return currentSlideInDots ? dotSlides.indexOf(currentSlideInDots) : -1;
  };

  return (
    <div className="boarding-pass-flow">
      <div
        className={`painted-gradient-container ${animationPhase === 'slide1-exit' ||
          (currentSlide > 1 && animationPhase !== 'slide1-enter')
          ? 'painted-gradient-container--expanded'
          : 'painted-gradient-container--collapsed'
          }`}
      >
        <div className="painted-gradient-static"></div>
      </div>

      {currentSlide === 1 && (
        <div
          className={`slide1-container ${animationPhase === 'slide1-exit' ? 'slide1-container--exit' : ''
            } ${animationPhase === 'slide1-enter' ? 'slide1-container--enter' : ''}`}
        >
          <div className="slides-topbar-main">
            <div className="logo-group logo-group--main">
              <div className="logo logo-main-container">
                <Logo
                  className="logo-main"
                  src={
                    homepageData?.level_62_logo_black?.url ||
                    homepageData?.level_62_logo_black
                  }
                  alt={
                    homepageData?.level_62_logo_black?.alt || 'Level 62 Logo'
                  }
                />
              </div>
            </div>
          </div>
          <CurrentSlideComponent
            onNext={handleNext}
            canProceed={canProceedFromCurrent}
          />
        </div>
      )}

      {currentSlide > 1 && (
        <div
          className={`slides-container ${animationPhase === 'slide2-enter' ? 'slides-container--enter' : ''
            } ${animationPhase === 'slide2-exit' ? 'slides-container--exit' : ''}`}
        >
          {currentSlide === 6 && (
            <div className="slides-topbar-main slides-topbar-main--with-exit">
              <div className="logo-group logo-group--main">
                <div className="logo logo-main-container">
                  <Logo
                    className="logo-main"
                    src={
                      homepageData?.level_62_logo_black?.url ||
                      homepageData?.level_62_logo_black
                    }
                    alt={
                      homepageData?.level_62_logo_black?.alt || 'Level 62 Logo'
                    }
                  />
                </div>
              </div>
              <div className="logo-group logo-group--accents">
                <button
                  className="exit-button"
                  onClick={() => setShowExitPopup(true)}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    setShowExitPopup(true);
                  }}
                  type="button"
                  aria-label="Exit experience"
                >
                  Exit X
                </button>
              </div>
            </div>
          )}

          {currentSlide !== 6 && (
            <div className="slides-topbar">
              <div className="dot-navigation">
                {dotSlides.map((slide, index) => {
                  const currentDotIndex = getCurrentDotIndex();
                  return (
                    <div
                      key={slide.id}
                      className={`dot ${currentDotIndex === index ? 'dot--active' : ''
                        } ${currentDotIndex > index ? 'dot--completed' : ''}`}
                    />
                  );
                })}
              </div>
              <div className="logo-group logo-group--accents">
                <button
                  className="exit-button"
                  onClick={() => setShowExitPopup(true)}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    setShowExitPopup(true);
                  }}
                  type="button"
                  aria-label="Exit experience"
                >
                  Exit X
                </button>
              </div>
            </div>
          )}

          <div className="slides-content">
            <div
              className={`slide-wrapper ${animationPhase === 'sliding'
                ? `slide-wrapper--${slideDirection}`
                : ''
                }`}
            >
              <CurrentSlideComponent
                onNext={handleNext}
                onBack={handlePrevious}
                canProceed={canProceedFromCurrent}
                showNavigation={true}
              />
            </div>
          </div>

          {currentSlide < 6 && currentSlide !== 5 && (
            <div className="game-navigation">
              <button
                className="btn-primary--back btn-primary"
                onClick={handlePrevious}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  if (currentSlide !== 1 && !isTransitioning) {
                    handlePrevious();
                  }
                }}
                disabled={currentSlide === 1 || isTransitioning}
                type="button"
              >
                Back
              </button>

              <button
                className="nav-button nav-button--next btn-primary inverted"
                onClick={handleNext}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  if (canProceedFromCurrent && !isTransitioning) {
                    handleNext();
                  }
                }}
                disabled={!canProceedFromCurrent || isTransitioning}
                type="button"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {(showExitPopup || isPopupClosing) && (
        <div
          className={`popup-overlay ${isPopupClosing ? 'popup-overlay--closing' : ''}`}
          onClick={handleOverlayClick}
          onTouchStart={(e) => {
            if (e.target === e.currentTarget) {
              e.stopPropagation();
            }
          }}
          onTouchEnd={(e) => {
            if (e.target === e.currentTarget) {
              e.preventDefault();
            }
          }}
        >
          <div
            className={`popup ${currentSlide === 6 ? 'popup--boarding-pass' : ''} ${isPopupClosing ? 'popup--closing' : ''}`}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            <button
              className="popup-close"
              onTouchEnd={handlePopupCloseTouch}
              onClick={handlePopupCloseClick}
              disabled={isPopupClosing}
              type="button"
              aria-label="Close popup"
            >
              <span>×</span>
            </button>
            <div className="popup-body">
              <h3>
                Are you sure you <br />
                want to exit?
              </h3>
              <p>
                {currentSlide === 6
                  ? 'You will lose this boarding pass if you exit this experience.'
                  : 'You will lose your progress.'}
              </p>
              <button
                className="popup-button btn-primary inverted"
                onTouchEnd={handleConfirmTouch}
                onClick={handleConfirmClick}
                disabled={isPopupClosing}
                type="button"
              >
                Yes, I'm sure
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoardingPassFlow;